// src/pages/EditQuotation.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, Download, Edit, Repeat, Save, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import mangrishLogo from "../assets/logo.jpeg";
import princePipesLogo from "../assets/products-banner.jpeg";
import princePipesLogo1 from "../assets/product-banner.jpeg";

// Extend jsPDF for autoTable
declare module "jspdf" {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
    autoTable: (options: any) => jsPDF;
  }
}

interface Product {
  _id: string;
  name: string;
  size: string;
  unit: string;
  mrp: number;
  hsncode?: string;
  gstpercentage?: number;
  // Add any other product fields you need
}

interface QuotationProduct {
  productId: string;
  name: string;
  size: string;
  qty: number;
  unit: string;
  mrp: number;
  discount1: number;
  discount2: number;
  discount: number;
  rate: number;
  amount: number;
  hsncode: string;
  gstpercentage: number;
}

interface AddressDetails {
  name: string;
  address: string;
  email: string;
  phone: string;
}

interface BankDetails {
  name: string;
  accountNo: string;
  branch: string;
  ifsc: string;
  bankName: string;
}

interface QuotationState {
  quotationNo: string;
  date: string;
  gstNumber: string;
  billTo: AddressDetails;
  shipTo: AddressDetails;
  products: QuotationProduct[];
  note: string;
  terms: string[];
  bankDetails: BankDetails;
  transportation: number;
  totalgstPercentage: number;
  type: string;
  discountType: string; // Added discountType field
}

const BANK_OPTIONS = [
  {
    id: 1,
    label: "Central Bank - Kodigehalli (5634831064)",
    value: {
      name: "MANGRISH",
      accountNo: "5634831064",
      branch: "Kodigehalli, Bangalore 560097",
      ifsc: "CBIN0283697",
      bankName: "CENTRAL BANK OF INDIA",
    },
  },
  {
    id: 2,
    label: "Central Bank - Kodigehalli (3072665777)",
    value: {
      name: "MANGRISH",
      accountNo: "3072665777",
      branch: "Kodigehalli, Bangalore 560097",
      ifsc: "CBIN0283697",
      bankName: "CENTRAL BANK OF INDIA",
    },
  },
  {
    id: 3,
    label: "Kotak Mahindra - Sahakarnagar (3072665777)",
    value: {
      name: "MANGRISH",
      accountNo: "3072665777",
      branch: "Sahakarnagar, Bangalore 560097",
      ifsc: "KKBK00008045",
      bankName: "KOTAK MAHINDRA BANK",
    },
  },
  {
    id: 4,
    label: "Manual Entry",
    value: "manual",
  },
];

const GST_NUMBERS = [
  {
    value: "29ACAFM2729C1ZG",
    label: "29ACAFM2729C1ZG - MAngrish (Partnership)",
  },
  {
    value: "29AAEPS9027R1ZP",
    label: "29AAEPS9027R1ZP - MANGRISH (Proprietor)",
  },
];

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage Discount" },
  { value: "flat", label: "Flat Discount" },
];

const EditQuotation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showProductModal, setShowProductModal] = useState(false);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState("");
  // Form state
  const [quotation, setQuotation] = useState<QuotationState>({
    quotationNo: "",
    date: new Date().toISOString().split("T")[0],
    gstNumber: "",
    billTo: {
      name: "",
      address: "",
      email: "",
      phone: "",
    },
    shipTo: {
      name: "",
      address: "",
      email: "",
      phone: "",
    },
    products: [],
    note: "",
    terms: [],
    bankDetails: {
      name: "",
      accountNo: "",
      branch: "",
      ifsc: "",
      bankName: "CENTRAL BANK OF INDIA",
    },
    transportation: 0,
    totalgstPercentage: 18,
    type: "quotation",
    discountType: "percentage", // Initialize discountType
  });

  // This useEffect should be at the top level, not inside any condition
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllProducts(response.data);
        setFilteredProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };

    fetchProducts();
  }, []);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Filter out already selected products initially
        const selectedProductIds = quotation.products.map((p) => p.productId);
        const availableProducts = response.data.filter(
          (p: Product) => !selectedProductIds.includes(p._id)
        );

        setAllProducts(response.data);
        setFilteredProducts(availableProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      }
    };

    fetchProducts();
  }, [quotation.products]); // Add quotation.products as dependency

  const handleProductSearch = (query: string) => {
    setProductSearch(query);

    // Get IDs of already selected products
    const selectedProductIds = quotation.products.map((p) => p.productId);

    setFilteredProducts(
      allProducts.filter(
        (p) =>
          // Only include products not already selected
          !selectedProductIds.includes(p._id) &&
          // And match the search query
          (p.name.toLowerCase().includes(query.toLowerCase()) ||
            p.size.toLowerCase().includes(query.toLowerCase()))
      )
    );
  };

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = response?.data?.quotation;
        console.log("response.data.quotation", response?.data?.quotation);

        setQuotation({
          quotationNo: data.quotationNo,
          date: new Date(data.date).toISOString().split("T")[0],
          gstNumber: data.gstNumber,
          billTo: data.billTo,
          shipTo: data.shipTo || {
            name: "",
            address: "",
            email: "",
            phone: "",
          },
          products: data.products,
          note: data.note || "",
          terms: data.terms || [],
          bankDetails: data.bank || {
            name: "",
            accountNo: "",
            branch: "",
            ifsc: "",
            bankName: "CENTRAL BANK OF INDIA",
          },
          transportation: data.transportation || 0,
          totalgstPercentage: data.totalgstPercentage || 18,
          type: data.type || "quotation",
          discountType: data.discountType || "percentage", // Set discountType from API or default
        });
      } catch (error) {
        console.error("Error fetching quotation:", error);
        toast.error("Failed to load quotation");
        navigate("/quotation/history");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotation();
  }, [id, navigate]);

  const updateProduct = (
    index: number,
    field: keyof QuotationProduct,
    value: any
  ) => {
    setQuotation((prev) => {
      const updatedProducts = [...prev.products];

      if (field === "mrp" || field === "rate") {
        value = Math.max(0, parseFloat(value) || 0);
      }
      if (field === "qty") {
        value = Math.max(1, parseInt(value) || 1);
      }
      if (field === "discount1" || field === "discount2") {
        value = Math.max(0, Math.min(100, parseFloat(value) || 0));
      }

      updatedProducts[index] = {
        ...updatedProducts[index],
        [field]: value,
      };

      if (field === "rate") {
        const rate = parseFloat(value) || 0;
        const mrp = updatedProducts[index].mrp;

        if (mrp > 0) {
          if (quotation.discountType === "percentage") {
            const discountFactor = rate / mrp;
            const totalDiscountPercent = (1 - discountFactor) * 100;
            const discount = totalDiscountPercent / 2;
            updatedProducts[index].discount1 = discount;
            updatedProducts[index].discount2 = discount;
          }
        }

        updatedProducts[index].amount = rate * updatedProducts[index].qty;
      } else if (["mrp", "discount1", "discount2", "qty"].includes(field)) {
        if (quotation.discountType === "percentage") {
          const discountFactor =
            ((100 - updatedProducts[index].discount1) *
              (100 - updatedProducts[index].discount2)) /
            10000;
          updatedProducts[index].rate =
            updatedProducts[index].mrp * discountFactor;
        } else {
          // For flat discount, calculate rate as MRP minus discount1 (assuming discount1 is the flat amount)
          updatedProducts[index].rate =
            updatedProducts[index].mrp - updatedProducts[index].discount1;
        }
        updatedProducts[index].amount =
          updatedProducts[index].rate * updatedProducts[index].qty;
      }

      return { ...prev, products: updatedProducts };
    });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!quotation.billTo.name.trim()) errors.billToName = "Name is required";
    if (!quotation.billTo.address.trim())
      errors.billToAddress = "Address is required";
    if (!quotation.billTo.phone.trim())
      errors.billToPhone = "Phone is required";
    if (!quotation.gstNumber.trim())
      errors.gstNumber = "GST Number is required";

    if (quotation.products.length === 0) {
      errors.products = "At least one product must be added";
    } else {
      quotation.products.forEach((product, index) => {
        if (product.mrp <= 0) {
          errors[
            `product-${index}-mrp`
          ] = `MRP for ${product.name} must be greater than zero`;
        }
        if (product.amount <= 0) {
          errors[
            `product-${index}-amount`
          ] = `Amount for ${product.name} must be greater than zero`;
        }
      });
    }

    if (!quotation.date.trim()) errors.date = "Date is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateProductTotal = () =>
    quotation.products.reduce((sum, product) => sum + product.amount, 0);
  const calculateSubtotal = () => {
    const productTotal = calculateProductTotal();
    return productTotal + quotation.transportation;
  };
  const calculateGst = () =>
    calculateSubtotal() * (quotation.totalgstPercentage / 100);
  const calculateTotal = () => calculateSubtotal() + calculateGst();

  const handleInputChange = (field: string, value: any) => {
    setQuotation((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedInputChange = <K extends keyof QuotationState>(
    parentField: K,
    field: keyof QuotationState[K],
    value: any
  ) => {
    setQuotation((prev) => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField] as object),
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstError = Object.keys(validationErrors)[0];
      if (firstError) {
        const element = document.querySelector(`[data-field="${firstError}"]`);
        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");  
      await axios.put(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/${id}`,
        {
          ...quotation,
          totalAmount: calculateTotal().toFixed(2),
          discountType: quotation.discountType, // Include discountType in the payload
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Quotation updated successfully!");
      navigate("/quotation/history");
    } catch (error) {
      console.error("Error updating quotation:", error);
      toast.error("Failed to update quotation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePDF = () => {
    try {
      const doc = new jsPDF();

      // Add logos if available
      if (princePipesLogo1)
        doc.addImage(princePipesLogo1, "PNG", 10, 8, 45, 45);
      if (princePipesLogo)
        doc.addImage(princePipesLogo, "JPEG", 155, 8, 45, 45);
      if (mangrishLogo) doc.addImage(mangrishLogo, "WEBP", 95, 8, 20, 20);

      // Company header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(53, 194, 240);
      doc.text("M/s. MANGRISH", 105, 35, { align: "center" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text("No. 7/4, 60 feet Road ,Gangaborewell Road,", 105, 47, {
        align: "center",
      });
      doc.text(
        "Amco Layout, Kotihosahalli, Kodigehalli Bengalore 560097",
        105,
        52,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.text(
        `Authorised Distributor M/s ${
          quotation.gstNumber === "29ACAFM2729C1ZG"
            ? "Prince Pipes and Fittings Limited"
            : "Astral Ltd- for Infrastructure Range"
        }`,
        105,
        42,
        { align: "center" }
      );
      doc.text(`GST No. : ${quotation.gstNumber || ""}`, 105, 57, {
        align: "center",
      });

      doc.setTextColor(21, 4, 252);
      doc.text(
        `Ph No.: 9845068745    E-Mail : ${
          quotation.gstNumber === "29ACAFM2729C1ZG"
            ? "mp@mangrishprince.com"
            : "mangrish123@gmail.com"
        }, ${
          quotation.quotationNo.startsWith("Q1")
            ? "santosh@mangrishprince.com"
            : ""
        }`,
        105,
        62,
        { align: "center" }
      );

      // Quotation header
      doc.setFillColor(0, 51, 102);
      doc.rect(0, 65, 210, 10, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(
        quotation.type === "pi" ? "PROFORMA INVOICE" : "QUOTATION",
        105,
        72,
        { align: "center" }
      );
      doc.setTextColor(0, 0, 0);

      // Bill To section
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Bill To", 15, 82);
      doc.setFont("helvetica", "normal");
      doc.text(`${quotation.billTo.name}`, 15, 87);
      const addressLines = quotation.billTo.address.split("\n");
      addressLines.forEach((line, i) => doc.text(line, 15, 92 + i * 5));
      const emailY = 92 + addressLines.length * 5;
      doc.text(`Email:- ${quotation.billTo.email}`, 15, emailY);
      doc.text(`Ph no. ${quotation.billTo.phone}`, 15, emailY + 5);

      // Date and Quotation No
      const formattedDate = new Date(quotation.date).toLocaleDateString(
        "en-GB"
      );
      doc.text(`Date :- ${formattedDate}`, 160, 82);
      doc.text(
        `${quotation.type === "pi" ? "PI" : "QT"} No. ${quotation.quotationNo}`,
        160,
        87
      );

      // Ship To section if exists
      let shipToY = emailY + 15;
      if (quotation.shipTo?.name) {
        doc.setFont("helvetica", "bold");
        doc.text("Ship TO", 15, shipToY);
        doc.setFont("helvetica", "normal");
        doc.text(quotation.shipTo.name, 15, shipToY + 5);
        const shipAddressLines = quotation.shipTo.address.split("\n");
        shipAddressLines.forEach((line, i) => {
          doc.text(line, 15, shipToY + 10 + i * 5);
        });
        shipToY += 15 + shipAddressLines.length * 5;
      }

      // Products table
      const tableData = quotation.products.map((product, index) => [
        `${index + 1}`,
        `${product.name}\n${product.size}`,
        product.hsncode || '',
        product.qty.toLocaleString(),
        product.unit,
        product.mrp.toFixed(0),
        `${product.discount || 0}%`,
        product.rate.toFixed(2),
        product.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
      ]);

      autoTable(doc, {
        startY: shipToY,
        head: [
          [
            { content: "Sl No", rowSpan: 1 },
            { content: "Prince Pipes", rowSpan: 1 },
            { content: "HSN Code", rowSpan: 1 },
            { content: "Qty", rowSpan: 1 },
            { content: "Unit", rowSpan: 1 },
            { content: "MRP", rowSpan: 1 },
            { content: "Disc%", rowSpan: 1 },
            { content: "Rate", rowSpan: 1 },
            { content: "Amount", rowSpan: 1 },
          ],
        ],
        body: tableData,
        headStyles: {
          fillColor: [255, 255, 4],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          valign: "middle",
        },
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: "linebreak",
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        margin: { left: 15, right: 10 },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 45 },
          2: { cellWidth: 20, halign: "center" },
          3: { cellWidth: 15, halign: "center" },
          4: { cellWidth: 15, halign: "center" },
          5: { cellWidth: 15, halign: "center" },
          6: { cellWidth: 15, halign: "center" },
          7: { cellWidth: 15, halign: "right" },
          8: { cellWidth: 20, halign: "right" },
        },
      });

      const finalY = doc.lastAutoTable.finalY;
      const subtotal = calculateSubtotal();
      const gst = calculateGst();
      const total = calculateTotal();

      autoTable(doc, {
        startY: finalY + 0,
        body: [
          [
            "Transportation",
            quotation.transportation
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          [
            "Sub total",
            subtotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          [
            `GST ${quotation.totalgstPercentage}%`,
            gst.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
          ],
          ["Total", total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")],
        ],
        styles: {
          fontSize: 9,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: {
            cellWidth: 40,
            fontStyle: "bold",
            halign: "right",
            fillColor: [220, 230, 255],
          },
          1: {
            cellWidth: 25,
            halign: "right",
            fillColor: [220, 230, 255],
          },
        },
        margin: { left: 130 },
        tableWidth: 55,
      });

      if (quotation.note) {
        doc.setFont("helvetica", "bold");
        doc.text(`Note: - ${quotation.note}`, 15, finalY + 35);
      }

      doc.setFont("helvetica", "bold");
      doc.text("Terms & conditions", 15, finalY + 45);
      doc.setFont("helvetica", "normal");
      quotation.terms.forEach((term, i) =>
        doc.text(`${i + 1}. ${term}`, 15, finalY + 50 + i * 5)
      );

      const bankDetailsY = finalY + 55 + quotation.terms.length * 5;
      doc.setFont("helvetica", "bold");
      doc.text("Bank Details", 15, bankDetailsY);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Payment favoring to: ${quotation.bankDetails.name}`,
        15,
        bankDetailsY + 5
      );
      doc.text(
        `Bank Name: ${quotation.bankDetails.bankName}`,
        15,
        bankDetailsY + 10
      );
      doc.text(
        `A/C No. : ${quotation.bankDetails.accountNo}`,
        15,
        bankDetailsY + 15
      );
      doc.text(
        `Branch : ${quotation.bankDetails.branch}`,
        15,
        bankDetailsY + 20
      );
      doc.text(
        `IFSC Code : ${quotation.bankDetails.ifsc}`,
        15,
        bankDetailsY + 25
      );

      // Signature
      doc.text(`For Mangrish,`, 140, bankDetailsY);
      const signatureY = bankDetailsY + 15;
      doc.setDrawColor(0);
      doc.line(140, signatureY, 190, signatureY);
      doc.setFontSize(10);
      doc.text("Authorised Signatory", 140, signatureY + 5);
      doc.text("Electrically generated bill", 140, signatureY + 10);

      // Save the PDF
      doc.save(
        `${quotation.type === "pi" ? "PI" : "Quotation"}_${
          quotation.quotationNo
        }.pdf`
      );
    } catch (err) {
      console.error("Failed to generate PDF", err);
      toast.error("Failed to generate PDF");
    }
  };

  const addTerm = () => {
    setQuotation((prev) => ({
      ...prev,
      terms: [...prev.terms, ""],
    }));
  };

  const updateTerm = (index: number, value: string) => {
    setQuotation((prev) => {
      const updatedTerms = [...prev.terms];
      updatedTerms[index] = value;
      return { ...prev, terms: updatedTerms };
    });
  };

  const removeTerm = (index: number) => {
    setQuotation((prev) => {
      const updatedTerms = [...prev.terms];
      updatedTerms.splice(index, 1);
      return { ...prev, terms: updatedTerms };
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  //   const handleProductSearch = (query: string) => {
  //     setProductSearch(query);
  //     setFilteredProducts(
  //       allProducts.filter(
  //         (p) =>
  //           p.name.toLowerCase().includes(query.toLowerCase()) ||
  //           p.size.toLowerCase().includes(query.toLowerCase())
  //       )
  //     );
  //   };

  const handleAddProduct = (product: Product) => {
    if (!product._id) {
      console.error("Product ID is missing");
      return;
    }

    const newProduct: QuotationProduct = {
      productId: product._id.toString(),
      name: product.name,
      size: product.size,
      qty: 1,
      unit: product.unit,
      mrp: product.mrp,
      discount1: 0,
      discount2: 0,
      discount: 0,
      rate: product.mrp,
      amount: product.mrp,
      hsncode: product.hsncode || "",
      gstpercentage: product.gstpercentage || 18,
    };

    setQuotation((prev) => ({
      ...prev,
      products: [...prev.products, newProduct],
    }));

    setValidationErrors((prev) => ({ ...prev, products: "" }));
    setShowProductModal(false);
    setProductSearch("");
  };

  //   useEffect(() => {
  //     const fetchProducts = async () => {
  //       try {
  //         const token = localStorage.getItem("token");
  //         const response = await axios.get(
  //           `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`,
  //           { headers: { Authorization: `Bearer ${token}` } }
  //         );
  //         setAllProducts(response.data);
  //         setFilteredProducts(response.data);
  //       } catch (error) {
  //         console.error("Error fetching products:", error);
  //         toast.error("Failed to load products");
  //       }
  //     };

  //     fetchProducts();
  //   }, []);
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">
                Edit{" "}
                {quotation.type === "pi" ? "Proforma Invoice" : "Quotation"}
              </h1>
              <p className="text-blue-100 opacity-90">
                {quotation.quotationNo}
              </p>
            </div>
            <button
              onClick={() => navigate("/quotation")}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
            >
              <ArrowLeft size={18} />
              Back to List
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
                Bill To
              </h3>
              <div className="mb-4" data-field="billToName">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.billToName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  value={quotation.billTo.name}
                  onChange={(e) =>
                    handleNestedInputChange("billTo", "name", e.target.value)
                  }
                />
                {validationErrors.billToName && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.billToName}
                  </p>
                )}
              </div>
              <div className="mb-4" data-field="billToAddress">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.billToAddress
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  rows={3}
                  value={quotation.billTo.address}
                  onChange={(e) =>
                    handleNestedInputChange("billTo", "address", e.target.value)
                  }
                />
                {validationErrors.billToAddress && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.billToAddress}
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quotation.billTo.email}
                  onChange={(e) =>
                    handleNestedInputChange("billTo", "email", e.target.value)
                  }
                />
              </div>
              <div className="mb-4" data-field="billToPhone">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.billToPhone
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  value={quotation.billTo.phone}
                  onChange={(e) =>
                    handleNestedInputChange("billTo", "phone", e.target.value)
                  }
                />
                {validationErrors.billToPhone && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.billToPhone}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
                Quotation Details
              </h3>
              <div className="mb-4" data-field="gstNumber">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number <span className="text-red-500">*</span>
                </label>
                <select
                  value={quotation.gstNumber}
                  onChange={(e) =>
                    handleInputChange("gstNumber", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.gstNumber
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                >
                  <option value="">Select GST Number</option>
                  {GST_NUMBERS.map((gst) => (
                    <option key={gst.value} value={gst.value}>
                      {gst.label}
                    </option>
                  ))}
                </select>
                {validationErrors.gstNumber && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.gstNumber}
                  </p>
                )}
              </div>
              <div className="mb-4" data-field="date">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.date ? "border-red-500" : "border-gray-300"
                  }`}
                  value={quotation.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                />
                {validationErrors.date && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.date}
                  </p>
                )}
              </div>
              {/* <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transportation (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quotation.transportation}
                  onChange={(e) =>
                    handleInputChange(
                      "transportation",
                      parseFloat(e.target.value) || 0
                    )
                  }
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Percentage (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quotation.totalgstPercentage}
                  onChange={(e) =>
                    handleInputChange(
                      "totalgstPercentage",
                      parseFloat(e.target.value) || 0
                    )
                  }
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={quotation.discountType}
                  onChange={(e) =>
                    handleInputChange("discountType", e.target.value)
                  }
                >
                  {DISCOUNT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div> */}
            </div>
          </div>

          {/* ... (rest of the JSX remains the same, except for the product table columns) ... */}

          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
                Products
              </h3>
              <button
                onClick={() => setShowProductModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center shadow-sm"
              >
                Add Product
              </button>
            </div>

            {validationErrors.products && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md"
                data-field="products"
              >
                {validationErrors.products}
              </div>
            )}

            {quotation.products.length > 0 ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HSN Code
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unit
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        MRP
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Disc%
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quotation.products.map((product, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">
                            {product.size} {product.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-gray-500">{product.hsncode}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="number"
                            min="1"
                            value={product.qty}
                            onChange={(e) =>
                              updateProduct(
                                idx,
                                "qty",
                                parseInt(e.target.value) || 1
                              )
                            }
                            className="w-16 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                          {product.unit}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={product.mrp}
                            onChange={(e) =>
                              updateProduct(
                                idx,
                                "mrp",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className={`w-20 border rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500 ${
                              validationErrors[`product-${idx}-mrp`]
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {validationErrors[`product-${idx}-mrp`] && (
                            <p className="text-xs text-red-500 mt-1">
                              {validationErrors[`product-${idx}-mrp`]}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={product.discount}
                            onChange={(e) =>
                              updateProduct(
                                idx,
                                "discount",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-16 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={product.rate}
                              onChange={(e) =>
                                updateProduct(
                                  idx,
                                  "rate",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-20 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div
                            className={`flex items-center justify-center ${
                              validationErrors[`product-${idx}-amount`]
                                ? "text-red-500"
                                : ""
                            }`}
                          >
                            ₹{product.amount.toFixed(2)}
                          </div>
                          {validationErrors[`product-${idx}-amount`] && (
                            <p className="text-xs text-red-500 mt-1">
                              {validationErrors[`product-${idx}-amount`]}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <button
                            onClick={() => {
                              setQuotation((prev) => {
                                const updatedProducts = [...prev.products];
                                updatedProducts.splice(idx, 1);
                                return { ...prev, products: updatedProducts };
                              });
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500 border border-gray-200">
                No products added yet.
              </div>
            )}

            <div className="flex justify-end mt-6">
              <div className="w-full md:w-96 border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 font-medium text-gray-700">
                        Transportation
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={quotation.transportation}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            handleInputChange(
                              "transportation",
                              value >= 0 ? value : 0
                            );
                          }}
                        />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 font-medium text-gray-700">
                        Sub total
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ₹{calculateSubtotal().toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="px-4 py-3 font-medium text-gray-700">
                        GST {quotation.totalgstPercentage}%
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ₹{calculateGst().toFixed(2)}
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 font-bold text-gray-800">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        ₹{calculateTotal().toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-bold text-gray-700 mb-2 text-lg">Note</h3>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={quotation.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
              placeholder="Additional notes..."
            />
          </div>

          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-gray-700 text-lg">
                Terms & Conditions
              </h3>
              <button
                onClick={addTerm}
                className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors flex items-center text-sm"
              >
                Add Term
              </button>
            </div>
            <div className="space-y-2">
              {quotation.terms.map((term, index) => (
                <div key={index} className="flex items-center">
                  <span className="mr-2 text-gray-600">{index + 1}.</span>
                  <input
                    type="text"
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={term}
                    onChange={(e) => updateTerm(index, e.target.value)}
                  />
                  <button
                    onClick={() => removeTerm(index)}
                    className="ml-2 p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
              Bank Details
            </h3>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Bank
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={JSON.stringify(quotation.bankDetails)}
                  onChange={(e) => {
                    if (e.target.value === "manual") {
                      handleInputChange("bankDetails", {
                        name: "",
                        accountNo: "",
                        branch: "",
                        ifsc: "",
                        bankName: "",
                      });
                    } else {
                      handleInputChange(
                        "bankDetails",
                        JSON.parse(e.target.value)
                      );
                    }
                  }}
                >
                  {BANK_OPTIONS.map((bank) => (
                    <option
                      key={bank.id}
                      value={
                        bank.value === "manual"
                          ? "manual"
                          : JSON.stringify(bank.value)
                      }
                    >
                      {bank.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment favoring to
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={quotation.bankDetails.name}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "bankDetails",
                        "name",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={quotation.bankDetails.bankName}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "bankDetails",
                        "bankName",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account Number
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={quotation.bankDetails.accountNo}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "bankDetails",
                        "accountNo",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={quotation.bankDetails.branch}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "bankDetails",
                        "branch",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={quotation.bankDetails.ifsc}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "bankDetails",
                        "ifsc",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 border-t pt-6">
            {/* <button
              onClick={handleGeneratePDF}
              disabled={isSubmitting}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={18} className="mr-2" />
              Generate PDF
            </button> */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex justify-between items-center border-b p-4">
              <h3 className="text-lg font-bold text-gray-800">
                Select Product
              </h3>
              <button
                onClick={() => setShowProductModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Search input */}
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="Search product by name or size..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={productSearch}
                onChange={(e) => handleProductSearch(e.target.value)}
              />
            </div>

            {/* Product list */}
            <div className="flex-grow overflow-y-auto">
              {filteredProducts.length > 0 ? (
                <ul className="divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <li
                      key={product._id}
                      className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => handleAddProduct(product)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-800">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            Size: {product.size} | MRP: ₹{product.mrp} | Unit:{" "}
                            {product.unit}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            HSN: {product.hsncode} | GST:{" "}
                            {product.gstpercentage}%
                          </div>
                        </div>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          Add
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {productSearch.trim()
                    ? "No matching products found"
                    : "All available products have been added"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditQuotation;
// // src/pages/EditQuotation.tsx
// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Eye, Download, Edit, Repeat, Save, ArrowLeft } from "lucide-react";
// import { toast } from "react-hot-toast";
// import { jsPDF } from "jspdf";
// import autoTable from "jspdf-autotable";
// import mangrishLogo from "../assets/logo.jpeg";
// import princePipesLogo from "../assets/products-banner.jpeg";
// import princePipesLogo1 from "../assets/product-banner.jpeg";

// // Extend jsPDF for autoTable
// declare module "jspdf" {
//   interface jsPDF {
//     lastAutoTable: {
//       finalY: number;
//     };
//     autoTable: (options: any) => jsPDF;
//   }
// }

// interface Product {
//   _id: string;
//   name: string;
//   size: string;
//   unit: string;
//   mrp: number;
//   hsncode?: string;
//   gstpercentage?: number;
//   // Add any other product fields you need
// }

// interface QuotationProduct {
//   productId: string;
//   name: string;
//   size: string;
//   qty: number;
//   unit: string;
//   mrp: number;
//   discount1: number;
//   discount2: number;
//   rate: number;
//   amount: number;
//   hsncode: string;
//   gstpercentage: number;
// }

// interface AddressDetails {
//   name: string;
//   address: string;
//   email: string;
//   phone: string;
// }

// interface BankDetails {
//   name: string;
//   accountNo: string;
//   branch: string;
//   ifsc: string;
//   bankName: string;
// }

// interface QuotationState {
//   quotationNo: string;
//   date: string;
//   gstNumber: string;
//   billTo: AddressDetails;
//   shipTo: AddressDetails;
//   products: QuotationProduct[];
//   note: string;
//   terms: string[];
//   bankDetails: BankDetails;
//   transportation: number;
//   totalgstPercentage: number;
//   type: string;
// }

// const BANK_OPTIONS = [
//   {
//     id: 1,
//     label: "Central Bank - Kodigehalli (5634831064)",
//     value: {
//       name: "MANGRISH",
//       accountNo: "5634831064",
//       branch: "Kodigehalli, Bangalore 560097",
//       ifsc: "CBIN0283697",
//       bankName: "CENTRAL BANK OF INDIA",
//     },
//   },
//   {
//     id: 2,
//     label: "Central Bank - Kodigehalli (3072665777)",
//     value: {
//       name: "MANGRISH",
//       accountNo: "3072665777",
//       branch: "Kodigehalli, Bangalore 560097",
//       ifsc: "CBIN0283697",
//       bankName: "CENTRAL BANK OF INDIA",
//     },
//   },
//   {
//     id: 3,
//     label: "Kotak Mahindra - Sahakarnagar (3072665777)",
//     value: {
//       name: "MANGRISH",
//       accountNo: "3072665777",
//       branch: "Sahakarnagar, Bangalore 560097",
//       ifsc: "KKBK00008045",
//       bankName: "KOTAK MAHINDRA BANK",
//     },
//   },
//   {
//     id: 4,
//     label: "Manual Entry",
//     value: "manual",
//   },
// ];

// const GST_NUMBERS = [
//   {
//     value: "29ACAFM2729C1ZG",
//     label: "29ACAFM2729C1ZG - MAngrish (Partnership)",
//   },
//   {
//     value: "29AAEPS9027R1ZP",
//     label: "29AAEPS9027R1ZP - MANGRISH (Proprietor)",
//   },
// ];

// const EditQuotation: React.FC = () => {
//   const { id } = useParams<{ id: string }>();
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(true);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [validationErrors, setValidationErrors] = useState<
//     Record<string, string>
//   >({});
//   const [showProductModal, setShowProductModal] = useState(false);
//   const [allProducts, setAllProducts] = useState<Product[]>([]);
//   const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
//   const [productSearch, setProductSearch] = useState("");
//   // Form state
//   const [quotation, setQuotation] = useState<QuotationState>({
//     quotationNo: "",
//     date: new Date().toISOString().split("T")[0],
//     gstNumber: "",
//     billTo: {
//       name: "",
//       address: "",
//       email: "",
//       phone: "",
//     },
//     shipTo: {
//       name: "",
//       address: "",
//       email: "",
//       phone: "",
//     },
//     products: [],
//     note: "",
//     terms: [],
//     bankDetails: {
//       name: "",
//       accountNo: "",
//       branch: "",
//       ifsc: "",
//       bankName: "CENTRAL BANK OF INDIA",
//     },
//     transportation: 0,
//     totalgstPercentage: 18,
//     type: "quotation",
//   });

//   useEffect(() => {
//     const fetchQuotation = async () => {
//       try {
//         const token = localStorage.getItem("token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/${id}`,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );

//         const data = response?.data;
//         console.log("response.data.quotation", response?.data);

//         setQuotation({
//           quotationNo: data.quotationNo,
//           date: new Date(data.date).toISOString().split("T")[0],
//           gstNumber: data.gstNumber,
//           billTo: data.billTo,
//           shipTo: data.shipTo || {
//             name: "",
//             address: "",
//             email: "",
//             phone: "",
//           },
//           products: data.products,
//           note: data.note || "",
//           terms: data.terms || [],
//           bankDetails: data.bank || {
//             name: "",
//             accountNo: "",
//             branch: "",
//             ifsc: "",
//             bankName: "CENTRAL BANK OF INDIA",
//           },
//           transportation: data.transportation || 0,
//           totalgstPercentage: data.totalgstPercentage || 18,
//           type: data.type || "quotation",
//         });
//       } catch (error) {
//         console.error("Error fetching quotation:", error);
//         toast.error("Failed to load quotation");
//         navigate("/quotations");
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchQuotation();
//   }, [id, navigate]);

//   const updateProduct = (
//     index: number,
//     field: keyof QuotationProduct,
//     value: any
//   ) => {
//     setQuotation((prev) => {
//       const updatedProducts = [...prev.products];

//       if (field === "mrp" || field === "rate") {
//         value = Math.max(0, parseFloat(value) || 0);
//       }
//       if (field === "qty") {
//         value = Math.max(1, parseInt(value) || 1);
//       }
//       if (field === "discount1" || field === "discount2") {
//         value = Math.max(0, Math.min(100, parseFloat(value) || 0));
//       }

//       updatedProducts[index] = {
//         ...updatedProducts[index],
//         [field]: value,
//       };

//       if (field === "rate") {
//         const rate = parseFloat(value) || 0;
//         const mrp = updatedProducts[index].mrp;

//         if (mrp > 0) {
//           const discountFactor = rate / mrp;
//           const totalDiscountPercent = (1 - discountFactor) * 100;
//           const discount = totalDiscountPercent / 2;
//           updatedProducts[index].discount1 = discount;
//           updatedProducts[index].discount2 = discount;
//         }

//         updatedProducts[index].amount = rate * updatedProducts[index].qty;
//       } else if (["mrp", "discount1", "discount2", "qty"].includes(field)) {
//         const discountFactor =
//           ((100 - updatedProducts[index].discount1) *
//             (100 - updatedProducts[index].discount2)) /
//           10000;
//         updatedProducts[index].rate =
//           updatedProducts[index].mrp * discountFactor;
//         updatedProducts[index].amount =
//           updatedProducts[index].rate * updatedProducts[index].qty;
//       }

//       return { ...prev, products: updatedProducts };
//     });
//   };

//   const validateForm = () => {
//     const errors: Record<string, string> = {};

//     if (!quotation.billTo.name.trim()) errors.billToName = "Name is required";
//     if (!quotation.billTo.address.trim())
//       errors.billToAddress = "Address is required";
//     if (!quotation.billTo.phone.trim())
//       errors.billToPhone = "Phone is required";
//     if (!quotation.gstNumber.trim())
//       errors.gstNumber = "GST Number is required";

//     if (quotation.products.length === 0) {
//       errors.products = "At least one product must be added";
//     } else {
//       quotation.products.forEach((product, index) => {
//         if (product.mrp <= 0) {
//           errors[
//             `product-${index}-mrp`
//           ] = `MRP for ${product.name} must be greater than zero`;
//         }
//         if (product.amount <= 0) {
//           errors[
//             `product-${index}-amount`
//           ] = `Amount for ${product.name} must be greater than zero`;
//         }
//       });
//     }

//     if (!quotation.date.trim()) errors.date = "Date is required";

//     setValidationErrors(errors);
//     return Object.keys(errors).length === 0;
//   };

//   const calculateSubtotal = () =>
//     quotation.products.reduce((sum, product) => sum + product.amount, 0) +
//     quotation.transportation;
//   const calculateGst = () =>
//     calculateSubtotal() * (quotation.totalgstPercentage / 100);
//   const calculateTotal = () => calculateSubtotal() + calculateGst();

//   const handleInputChange = (field: string, value: any) => {
//     setQuotation((prev) => ({ ...prev, [field]: value }));
//   };

//   const handleNestedInputChange = <K extends keyof QuotationState>(
//     parentField: K,
//     field: keyof QuotationState[K],
//     value: any
//   ) => {
//     setQuotation((prev) => ({
//       ...prev,
//       [parentField]: {
//         ...(prev[parentField] as object),
//         [field]: value,
//       },
//     }));
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) {
//       const firstError = Object.keys(validationErrors)[0];
//       if (firstError) {
//         const element = document.querySelector(`[data-field="${firstError}"]`);
//         element?.scrollIntoView({ behavior: "smooth", block: "center" });
//       }
//       return;
//     }
//     setIsSubmitting(true);

//     try {
//       const token = localStorage.getItem("token");
//       await axios.put(
//         `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/quotations/${id}`,
//         {
//           ...quotation,
//           totalAmount: calculateTotal().toFixed(2),
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );

//       toast.success("Quotation updated successfully!");
//       navigate("/quotations");
//     } catch (error) {
//       console.error("Error updating quotation:", error);
//       toast.error("Failed to update quotation");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   const handleGeneratePDF = () => {
//     try {
//       const doc = new jsPDF();

//       // Add logos if available
//       if (princePipesLogo1)
//         doc.addImage(princePipesLogo1, "PNG", 10, 8, 45, 45);
//       if (princePipesLogo)
//         doc.addImage(princePipesLogo, "JPEG", 155, 8, 45, 45);
//       if (mangrishLogo) doc.addImage(mangrishLogo, "WEBP", 95, 8, 20, 20);

//       // Company header
//       doc.setFontSize(16);
//       doc.setFont("helvetica", "bold");
//       doc.setTextColor(53, 194, 240);
//       doc.text("M/s. MANGRISH", 105, 35, { align: "center" });

//       doc.setFontSize(10);
//       doc.setFont("helvetica", "normal");
//       doc.setTextColor(0, 0, 0);
//       doc.text("No. 7/4, 60 feet Road ,Gangaborewell Road,", 105, 47, {
//         align: "center",
//       });
//       doc.text(
//         "Amco Layout, Kotihosahalli, Kodigehalli Bengalore 560097",
//         105,
//         52,
//         { align: "center" }
//       );

//       doc.setFont("helvetica", "normal");
//       doc.text(
//         `Authorised Distributor M/s ${
//           quotation.gstNumber === "29ACAFM2729C1ZG"
//             ? "Prince Pipes and Fittings Limited"
//             : "Astral Ltd- for Infrastructure Range"
//         }`,
//         105,
//         42,
//         { align: "center" }
//       );
//       doc.text(`GST No. : ${quotation.gstNumber || ""}`, 105, 57, {
//         align: "center",
//       });

//       doc.setTextColor(21, 4, 252);
//       doc.text(
//         `Ph No.: 9845068745    E-Mail : ${
//           quotation.gstNumber === "29ACAFM2729C1ZG"
//             ? "mp@mangrishprince.com"
//             : "mangrish123@gmail.com"
//         }, ${
//           quotation.quotationNo.startsWith("Q1")
//             ? "santosh@mangrishprince.com"
//             : ""
//         }`,
//         105,
//         62,
//         { align: "center" }
//       );

//       // Quotation header
//       doc.setFillColor(0, 51, 102);
//       doc.rect(0, 65, 210, 10, "F");
//       doc.setTextColor(255, 255, 255);
//       doc.setFontSize(14);
//       doc.setFont("helvetica", "bold");
//       doc.text(
//         quotation.type === "pi" ? "PROFORMA INVOICE" : "QUOTATION",
//         105,
//         72,
//         { align: "center" }
//       );
//       doc.setTextColor(0, 0, 0);

//       // Bill To section
//       doc.setFontSize(10);
//       doc.setFont("helvetica", "bold");
//       doc.text("Bill To", 15, 82);
//       doc.setFont("helvetica", "normal");
//       doc.text(`${quotation.billTo.name}`, 15, 87);
//       const addressLines = quotation.billTo.address.split("\n");
//       addressLines.forEach((line, i) => doc.text(line, 15, 92 + i * 5));
//       const emailY = 92 + addressLines.length * 5;
//       doc.text(`Email:- ${quotation.billTo.email}`, 15, emailY);
//       doc.text(`Ph no. ${quotation.billTo.phone}`, 15, emailY + 5);

//       // Date and Quotation No
//       const formattedDate = new Date(quotation.date).toLocaleDateString(
//         "en-GB"
//       );
//       doc.text(`Date :- ${formattedDate}`, 160, 82);
//       doc.text(
//         `${quotation.type === "pi" ? "PI" : "QT"} No. ${quotation.quotationNo}`,
//         160,
//         87
//       );

//       // Ship To section if exists
//       let shipToY = emailY + 15;
//       if (quotation.shipTo?.name) {
//         doc.setFont("helvetica", "bold");
//         doc.text("Ship TO", 15, shipToY);
//         doc.setFont("helvetica", "normal");
//         doc.text(quotation.shipTo.name, 15, shipToY + 5);
//         const shipAddressLines = quotation.shipTo.address.split("\n");
//         shipAddressLines.forEach((line, i) => {
//           doc.text(line, 15, shipToY + 10 + i * 5);
//         });
//         shipToY += 15 + shipAddressLines.length * 5;
//       }

//       // Products table
//       const tableData = quotation.products.map((product, index) => [
//         `${index + 1}`,
//         `${product.name}\n${product.size}`,
//         product.qty.toLocaleString(),
//         product.unit,
//         product.mrp.toFixed(0),
//         `${product.discount1}%`,
//         `${product.discount2}%`,
//         product.rate.toFixed(2),
//         product.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
//       ]);

//       autoTable(doc, {
//         startY: shipToY,
//         head: [
//           [
//             { content: "Sl No", rowSpan: 2 },
//             { content: "Prince Pipes", rowSpan: 2 },
//             { content: "Qty", rowSpan: 2 },
//             { content: "Unit", rowSpan: 2 },
//             { content: "MRP", rowSpan: 2 },
//             { content: "Discount", colSpan: 2 },
//             { content: "Rate", rowSpan: 2 },
//             { content: "Amount", rowSpan: 2 },
//           ],
//           [
//             {},
//             {},
//             {},
//             {},
//             {},
//             { content: "Disc1" },
//             { content: "Disc2" },
//             {},
//             {},
//           ],
//         ],
//         body: tableData,
//         headStyles: {
//           fillColor: [255, 255, 4],
//           textColor: [0, 0, 0],
//           fontStyle: "bold",
//           halign: "center",
//           valign: "middle",
//           lineColor: [0, 0, 0],
//           lineWidth: 0.2,
//         },
//         bodyStyles: {
//           lineColor: [0, 0, 0],
//           lineWidth: 0.1,
//           valign: "middle",
//         },
//         styles: {
//           fontSize: 9,
//           cellPadding: 2,
//           overflow: "linebreak",
//           lineColor: [0, 0, 0],
//           lineWidth: 0.1,
//         },
//         margin: { left: 15, right: 10 },
//         columnStyles: {
//           0: { cellWidth: 15, halign: "center" },
//           1: { cellWidth: 55 },
//           2: { cellWidth: 15, halign: "center" },
//           3: { cellWidth: 15, halign: "center" },
//           4: { cellWidth: 15, halign: "center" },
//           5: { cellWidth: 15, halign: "center" },
//           6: { cellWidth: 15, halign: "center" },
//           7: { cellWidth: 15, halign: "right" },
//           8: { cellWidth: 20, halign: "right" },
//         },
//       });

//       const finalY = doc.lastAutoTable.finalY;
//       const subtotal = calculateSubtotal();
//       const gst = calculateGst();
//       const total = calculateTotal();

//       autoTable(doc, {
//         startY: finalY + 0,
//         body: [
//           [
//             "Transportation",
//             quotation.transportation
//               .toFixed(2)
//               .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
//           ],
//           [
//             "Sub total",
//             subtotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
//           ],
//           [
//             `GST ${quotation.totalgstPercentage}%`,
//             gst.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","),
//           ],
//           ["Total", total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")],
//         ],
//         styles: {
//           fontSize: 9,
//           cellPadding: 2,
//           lineColor: [0, 0, 0],
//           lineWidth: 0.1,
//         },
//         columnStyles: {
//           0: {
//             cellWidth: 40,
//             fontStyle: "bold",
//             halign: "right",
//             fillColor: [220, 230, 255],
//           },
//           1: {
//             cellWidth: 25,
//             halign: "right",
//             fillColor: [220, 230, 255],
//           },
//         },
//         margin: { left: 130 },
//         tableWidth: 55,
//       });

//       if (quotation.note) {
//         doc.setFont("helvetica", "bold");
//         doc.text(`Note: - ${quotation.note}`, 15, finalY + 35);
//       }

//       doc.setFont("helvetica", "bold");
//       doc.text("Terms & conditions", 15, finalY + 45);
//       doc.setFont("helvetica", "normal");
//       quotation.terms.forEach((term, i) =>
//         doc.text(`${i + 1}. ${term}`, 15, finalY + 50 + i * 5)
//       );

//       const bankDetailsY = finalY + 55 + quotation.terms.length * 5;
//       doc.setFont("helvetica", "bold");
//       doc.text("Bank Details", 15, bankDetailsY);
//       doc.setFont("helvetica", "normal");
//       doc.text(
//         `Payment favoring to: ${quotation.bankDetails.name}`,
//         15,
//         bankDetailsY + 5
//       );
//       doc.text(
//         `Bank Name: ${quotation.bankDetails.bankName}`,
//         15,
//         bankDetailsY + 10
//       );
//       doc.text(
//         `A/C No. : ${quotation.bankDetails.accountNo}`,
//         15,
//         bankDetailsY + 15
//       );
//       doc.text(
//         `Branch : ${quotation.bankDetails.branch}`,
//         15,
//         bankDetailsY + 20
//       );
//       doc.text(
//         `IFSC Code : ${quotation.bankDetails.ifsc}`,
//         15,
//         bankDetailsY + 25
//       );

//       // Signature
//       doc.text(`For Mangrish,`, 140, bankDetailsY);
//       const signatureY = bankDetailsY + 15;
//       doc.setDrawColor(0);
//       doc.line(140, signatureY, 190, signatureY);
//       doc.setFontSize(10);
//       doc.text("Authorised Signatory", 140, signatureY + 5);
//       doc.text("Electrically generated bill", 140, signatureY + 10);

//       // Save the PDF
//       doc.save(
//         `${quotation.type === "pi" ? "PI" : "Quotation"}_${
//           quotation.quotationNo
//         }.pdf`
//       );
//     } catch (err) {
//       console.error("Failed to generate PDF", err);
//       toast.error("Failed to generate PDF");
//     }
//   };

//   const addTerm = () => {
//     setQuotation((prev) => ({
//       ...prev,
//       terms: [...prev.terms, ""],
//     }));
//   };

//   const updateTerm = (index: number, value: string) => {
//     setQuotation((prev) => {
//       const updatedTerms = [...prev.terms];
//       updatedTerms[index] = value;
//       return { ...prev, terms: updatedTerms };
//     });
//   };

//   const removeTerm = (index: number) => {
//     setQuotation((prev) => {
//       const updatedTerms = [...prev.terms];
//       updatedTerms.splice(index, 1);
//       return { ...prev, terms: updatedTerms };
//     });
//   };

//   if (isLoading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//       </div>
//     );
//   }

//   const handleProductSearch = (query: string) => {
//     setProductSearch(query);
//     setFilteredProducts(
//       allProducts.filter(
//         (p) =>
//           p.name.toLowerCase().includes(query.toLowerCase()) ||
//           p.size.toLowerCase().includes(query.toLowerCase())
//       )
//     );
//   };

//   const handleAddProduct = (product: Product) => {
//     if (!product._id) {
//       console.error("Product ID is missing");
//       return;
//     }

//     const newProduct: QuotationProduct = {
//       productId: product._id.toString(),
//       name: product.name,
//       size: product.size,
//       qty: 1,
//       unit: product.unit,
//       mrp: product.mrp,
//       discount1: 0,
//       discount2: 0,
//       rate: product.mrp,
//       amount: product.mrp,
//       hsncode: product.hsncode || "",
//       gstpercentage: product.gstpercentage || 18,
//     };

//     setQuotation((prev) => ({
//       ...prev,
//       products: [...prev.products, newProduct],
//     }));

//     setValidationErrors((prev) => ({ ...prev, products: "" }));
//     setShowProductModal(false);
//     setProductSearch("");
//   };

//   useEffect(() => {
//     const fetchProducts = async () => {
//       try {
//         const token = localStorage.getItem("token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         setAllProducts(response.data);
//         setFilteredProducts(response.data);
//       } catch (error) {
//         console.error("Error fetching products:", error);
//         toast.error("Failed to load products");
//       }
//     };

//     fetchProducts();
//   }, []);

//   return (
//     <div className="min-h-screen bg-gray-50 p-4 md:p-6">
//       <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
//         <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
//           <div className="flex justify-between items-center">
//             <div>
//               <h1 className="text-2xl font-bold">
//                 Edit{" "}
//                 {quotation.type === "pi" ? "Proforma Invoice" : "Quotation"}
//               </h1>
//               <p className="text-blue-100 opacity-90">
//                 {quotation.quotationNo}
//               </p>
//             </div>
//             <button
//               onClick={() => navigate("/quotations")}
//               className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-md transition-colors"
//             >
//               <ArrowLeft size={18} />
//               Back to List
//             </button>
//           </div>
//         </div>

//         <div className="p-6">
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
//             <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
//               <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
//                 Bill To
//               </h3>
//               <div className="mb-4" data-field="billToName">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Name <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="text"
//                   className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
//                     validationErrors.billToName
//                       ? "border-red-500"
//                       : "border-gray-300"
//                   }`}
//                   value={quotation.billTo.name}
//                   onChange={(e) =>
//                     handleNestedInputChange("billTo", "name", e.target.value)
//                   }
//                 />
//                 {validationErrors.billToName && (
//                   <p className="mt-1 text-sm text-red-600">
//                     {validationErrors.billToName}
//                   </p>
//                 )}
//               </div>
//               <div className="mb-4" data-field="billToAddress">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Address <span className="text-red-500">*</span>
//                 </label>
//                 <textarea
//                   className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
//                     validationErrors.billToAddress
//                       ? "border-red-500"
//                       : "border-gray-300"
//                   }`}
//                   rows={3}
//                   value={quotation.billTo.address}
//                   onChange={(e) =>
//                     handleNestedInputChange("billTo", "address", e.target.value)
//                   }
//                 />
//                 {validationErrors.billToAddress && (
//                   <p className="mt-1 text-sm text-red-600">
//                     {validationErrors.billToAddress}
//                   </p>
//                 )}
//               </div>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Email
//                 </label>
//                 <input
//                   type="email"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={quotation.billTo.email}
//                   onChange={(e) =>
//                     handleNestedInputChange("billTo", "email", e.target.value)
//                   }
//                 />
//               </div>
//               <div className="mb-4" data-field="billToPhone">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Phone <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="tel"
//                   className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
//                     validationErrors.billToPhone
//                       ? "border-red-500"
//                       : "border-gray-300"
//                   }`}
//                   value={quotation.billTo.phone}
//                   onChange={(e) =>
//                     handleNestedInputChange("billTo", "phone", e.target.value)
//                   }
//                 />
//                 {validationErrors.billToPhone && (
//                   <p className="mt-1 text-sm text-red-600">
//                     {validationErrors.billToPhone}
//                   </p>
//                 )}
//               </div>
//             </div>

//             <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
//               <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
//                 Quotation Details
//               </h3>
//               <div className="mb-4" data-field="gstNumber">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   GST Number <span className="text-red-500">*</span>
//                 </label>
//                 <select
//                   value={quotation.gstNumber}
//                   onChange={(e) =>
//                     handleInputChange("gstNumber", e.target.value)
//                   }
//                   className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
//                     validationErrors.gstNumber
//                       ? "border-red-500"
//                       : "border-gray-300"
//                   }`}
//                   required
//                 >
//                   <option value="">Select GST Number</option>
//                   {GST_NUMBERS.map((gst) => (
//                     <option key={gst.value} value={gst.value}>
//                       {gst.label}
//                     </option>
//                   ))}
//                 </select>
//                 {validationErrors.gstNumber && (
//                   <p className="mt-1 text-sm text-red-600">
//                     {validationErrors.gstNumber}
//                   </p>
//                 )}
//               </div>
//               <div className="mb-4" data-field="date">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Date <span className="text-red-500">*</span>
//                 </label>
//                 <input
//                   type="date"
//                   className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
//                     validationErrors.date ? "border-red-500" : "border-gray-300"
//                   }`}
//                   value={quotation.date}
//                   onChange={(e) => handleInputChange("date", e.target.value)}
//                 />
//                 {validationErrors.date && (
//                   <p className="mt-1 text-sm text-red-600">
//                     {validationErrors.date}
//                   </p>
//                 )}
//               </div>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Transportation (₹)
//                 </label>
//                 <input
//                   type="number"
//                   min="0"
//                   step="0.01"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={quotation.transportation}
//                   onChange={(e) =>
//                     handleInputChange(
//                       "transportation",
//                       parseFloat(e.target.value) || 0
//                     )
//                   }
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   GST Percentage (%)
//                 </label>
//                 <input
//                   type="number"
//                   min="0"
//                   max="100"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={quotation.totalgstPercentage}
//                   onChange={(e) =>
//                     handleInputChange(
//                       "totalgstPercentage",
//                       parseFloat(e.target.value) || 0
//                     )
//                   }
//                 />
//               </div>
//             </div>
//           </div>

//           <div className="bg-gray-50 p-4 rounded-lg mb-8 border border-gray-200">
//             <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
//               Ship To (Optional)
//             </h3>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Name
//                 </label>
//                 <input
//                   type="text"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={quotation.shipTo.name}
//                   onChange={(e) =>
//                     handleNestedInputChange("shipTo", "name", e.target.value)
//                   }
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Phone
//                 </label>
//                 <input
//                   type="tel"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={quotation.shipTo.phone}
//                   onChange={(e) =>
//                     handleNestedInputChange("shipTo", "phone", e.target.value)
//                   }
//                 />
//               </div>
//             </div>
//             <div className="mt-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Address
//               </label>
//               <textarea
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 rows={3}
//                 value={quotation.shipTo.address}
//                 onChange={(e) =>
//                   handleNestedInputChange("shipTo", "address", e.target.value)
//                 }
//               />
//             </div>
//             <div className="mt-4">
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Email
//               </label>
//               <input
//                 type="email"
//                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                 value={quotation.shipTo.email}
//                 onChange={(e) =>
//                   handleNestedInputChange("shipTo", "email", e.target.value)
//                 }
//               />
//             </div>
//           </div>

//           <div className="mb-8">
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
//                 Products
//               </h3>
//               <button
//                 onClick={() => setShowProductModal(true)}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center shadow-sm"
//               >
//                 Add Product
//               </button>
//             </div>

//             {validationErrors.products && (
//               <div
//                 className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md"
//                 data-field="products"
//               >
//                 {validationErrors.products}
//               </div>
//             )}

//             {quotation.products.length > 0 ? (
//               <div className="overflow-x-auto border border-gray-200 rounded-lg">
//                 <table className="min-w-full divide-y divide-gray-200">
//                   <thead className="bg-gray-50">
//                     <tr>
//                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Product
//                       </th>
//                       <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Qty
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Unit
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         MRP
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Disc 1%
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Disc 2%
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Rate
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Amount
//                       </th>
//                       <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
//                         Action
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody className="bg-white divide-y divide-gray-200">
//                     {quotation.products.map((product, idx) => (
//                       <tr
//                         key={idx}
//                         className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
//                       >
//                         <td className="px-4 py-4 whitespace-nowrap">
//                           <div className="font-medium text-gray-900">
//                             {product.name}
//                           </div>
//                           <div className="text-sm text-gray-500 mt-1">
//                             Size: {product.size} | HSN: {product.hsncode} | GST:{" "}
//                             {product.gstpercentage}%
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <input
//                             type="number"
//                             min="1"
//                             value={product.qty}
//                             onChange={(e) =>
//                               updateProduct(
//                                 idx,
//                                 "qty",
//                                 parseInt(e.target.value) || 1
//                               )
//                             }
//                             className="w-16 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
//                           />
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
//                           {product.unit}
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <input
//                             type="number"
//                             step="0.01"
//                             min="0"
//                             value={product.mrp}
//                             onChange={(e) =>
//                               updateProduct(
//                                 idx,
//                                 "mrp",
//                                 parseFloat(e.target.value) || 0
//                               )
//                             }
//                             className={`w-20 border rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500 ${
//                               validationErrors[`product-${idx}-mrp`]
//                                 ? "border-red-500"
//                                 : "border-gray-300"
//                             }`}
//                           />
//                           {validationErrors[`product-${idx}-mrp`] && (
//                             <p className="text-xs text-red-500 mt-1">
//                               {validationErrors[`product-${idx}-mrp`]}
//                             </p>
//                           )}
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <input
//                             type="number"
//                             min="0"
//                             max="100"
//                             value={product.discount1}
//                             onChange={(e) =>
//                               updateProduct(
//                                 idx,
//                                 "discount1",
//                                 parseFloat(e.target.value) || 0
//                               )
//                             }
//                             className="w-16 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
//                           />
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <input
//                             type="number"
//                             min="0"
//                             max="100"
//                             value={product.discount2}
//                             onChange={(e) =>
//                               updateProduct(
//                                 idx,
//                                 "discount2",
//                                 parseFloat(e.target.value) || 0
//                               )
//                             }
//                             className="w-16 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
//                           />
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <div className="flex items-center justify-center">
//                             <input
//                               type="number"
//                               step="0.01"
//                               min="0"
//                               value={product.rate}
//                               onChange={(e) =>
//                                 updateProduct(
//                                   idx,
//                                   "rate",
//                                   parseFloat(e.target.value) || 0
//                                 )
//                               }
//                               className="w-20 border border-gray-300 rounded-md px-2 py-1 text-center focus:ring-2 focus:ring-blue-500"
//                             />
//                           </div>
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center">
//                           <div
//                             className={`flex items-center justify-center ${
//                               validationErrors[`product-${idx}-amount`]
//                                 ? "text-red-500"
//                                 : ""
//                             }`}
//                           >
//                             ₹{product.amount.toFixed(2)}
//                           </div>
//                           {validationErrors[`product-${idx}-amount`] && (
//                             <p className="text-xs text-red-500 mt-1">
//                               {validationErrors[`product-${idx}-amount`]}
//                             </p>
//                           )}
//                         </td>
//                         <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
//                           <button
//                             onClick={() => {
//                               setQuotation((prev) => {
//                                 const updatedProducts = [...prev.products];
//                                 updatedProducts.splice(idx, 1);
//                                 return { ...prev, products: updatedProducts };
//                               });
//                             }}
//                             className="text-red-600 hover:text-red-900"
//                           >
//                             Remove
//                           </button>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             ) : (
//               <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500 border border-gray-200">
//                 No products added yet.
//               </div>
//             )}

//             <div className="flex justify-end mt-6">
//               <div className="w-full md:w-96 border border-gray-200 rounded-lg overflow-hidden">
//                 <table className="w-full">
//                   <tbody>
//                     <tr className="border-b border-gray-200">
//                       <td className="px-4 py-3 font-medium text-gray-700">
//                         Transportation
//                       </td>
//                       <td className="px-4 py-3 text-right">
//                         ₹{quotation.transportation.toFixed(2)}
//                       </td>
//                     </tr>
//                     <tr className="border-b border-gray-200">
//                       <td className="px-4 py-3 font-medium text-gray-700">
//                         Sub total
//                       </td>
//                       <td className="px-4 py-3 text-right font-medium">
//                         ₹{calculateSubtotal().toFixed(2)}
//                       </td>
//                     </tr>
//                     <tr className="border-b border-gray-200">
//                       <td className="px-4 py-3 font-medium text-gray-700">
//                         GST {quotation.totalgstPercentage}%
//                       </td>
//                       <td className="px-4 py-3 text-right font-medium">
//                         ₹{calculateGst().toFixed(2)}
//                       </td>
//                     </tr>
//                     <tr className="bg-gray-50">
//                       <td className="px-4 py-3 font-bold text-gray-800">
//                         Total
//                       </td>
//                       <td className="px-4 py-3 text-right font-bold text-gray-800">
//                         ₹{calculateTotal().toFixed(2)}
//                       </td>
//                     </tr>
//                   </tbody>
//                 </table>
//               </div>
//             </div>
//           </div>

//           <div className="mb-8">
//             <h3 className="font-bold text-gray-700 mb-2 text-lg">Note</h3>
//             <textarea
//               className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//               rows={3}
//               value={quotation.note}
//               onChange={(e) => handleInputChange("note", e.target.value)}
//               placeholder="Additional notes..."
//             />
//           </div>

//           <div className="mb-8">
//             <div className="flex justify-between items-center mb-2">
//               <h3 className="font-bold text-gray-700 text-lg">
//                 Terms & Conditions
//               </h3>
//               <button
//                 onClick={addTerm}
//                 className="px-3 py-1 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors flex items-center text-sm"
//               >
//                 Add Term
//               </button>
//             </div>
//             <div className="space-y-2">
//               {quotation.terms.map((term, index) => (
//                 <div key={index} className="flex items-center">
//                   <span className="mr-2 text-gray-600">{index + 1}.</span>
//                   <input
//                     type="text"
//                     className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={term}
//                     onChange={(e) => updateTerm(index, e.target.value)}
//                   />
//                   <button
//                     onClick={() => removeTerm(index)}
//                     className="ml-2 p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
//                   >
//                     Remove
//                   </button>
//                 </div>
//               ))}
//             </div>
//           </div>

//           <div className="mb-8">
//             <h3 className="font-bold text-gray-700 mb-4 text-lg border-b pb-2">
//               Bank Details
//             </h3>
//             <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
//               <div className="mb-4">
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Select Bank
//                 </label>
//                 <select
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                   value={JSON.stringify(quotation.bankDetails)}
//                   onChange={(e) => {
//                     if (e.target.value === "manual") {
//                       handleInputChange("bankDetails", {
//                         name: "",
//                         accountNo: "",
//                         branch: "",
//                         ifsc: "",
//                         bankName: "",
//                       });
//                     } else {
//                       handleInputChange(
//                         "bankDetails",
//                         JSON.parse(e.target.value)
//                       );
//                     }
//                   }}
//                 >
//                   {BANK_OPTIONS.map((bank) => (
//                     <option
//                       key={bank.id}
//                       value={
//                         bank.value === "manual"
//                           ? "manual"
//                           : JSON.stringify(bank.value)
//                       }
//                     >
//                       {bank.label}
//                     </option>
//                   ))}
//                 </select>
//               </div>

//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Payment favoring to
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={quotation.bankDetails.name}
//                     onChange={(e) =>
//                       handleNestedInputChange(
//                         "bankDetails",
//                         "name",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Bank Name
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={quotation.bankDetails.bankName}
//                     onChange={(e) =>
//                       handleNestedInputChange(
//                         "bankDetails",
//                         "bankName",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Account Number
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={quotation.bankDetails.accountNo}
//                     onChange={(e) =>
//                       handleNestedInputChange(
//                         "bankDetails",
//                         "accountNo",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Branch
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={quotation.bankDetails.branch}
//                     onChange={(e) =>
//                       handleNestedInputChange(
//                         "bankDetails",
//                         "branch",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </div>
//                 <div className="md:col-span-2">
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     IFSC Code
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     value={quotation.bankDetails.ifsc}
//                     onChange={(e) =>
//                       handleNestedInputChange(
//                         "bankDetails",
//                         "ifsc",
//                         e.target.value
//                       )
//                     }
//                   />
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 border-t pt-6">
//             <button
//               onClick={handleGeneratePDF}
//               disabled={isSubmitting}
//               className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               <Download size={18} className="mr-2" />
//               Generate PDF
//             </button>
//             <button
//               onClick={handleSubmit}
//               disabled={isSubmitting}
//               className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {isSubmitting ? (
//                 <>
//                   <svg
//                     className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                   >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     ></circle>
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                     ></path>
//                   </svg>
//                   Saving...
//                 </>
//               ) : (
//                 <>
//                   <Save size={18} className="mr-2" />
//                   Save Changes
//                 </>
//               )}
//             </button>
//           </div>
//         </div>
//       </div>
//       {showProductModal && (
//   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//     <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
//       <div className="flex justify-between items-center border-b p-4">
//         <h3 className="text-lg font-bold text-gray-800">Select Product</h3>
//         <button
//           onClick={() => setShowProductModal(false)}
//           className="text-gray-500 hover:text-gray-700"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//           </svg>
//         </button>
//       </div>
//       <div className="p-4 border-b">
//         <input
//           type="text"
//           placeholder="Search product by name or size..."
//           className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//           value={productSearch}
//           onChange={(e) => handleProductSearch(e.target.value)}
//         />
//       </div>
//       <div className="flex-grow overflow-y-auto">
//         {filteredProducts.length > 0 ? (
//           <ul className="divide-y divide-gray-200">
//             {filteredProducts.map((product) => (
//               <li
//                 key={product._id}
//                 className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
//                 onClick={() => handleAddProduct(product)}
//               >
//                 <div className="flex justify-between items-start">
//                   <div>
//                     <div className="font-medium text-gray-800">{product.name}</div>
//                     <div className="text-sm text-gray-500 mt-1">
//                       Size: {product.size} | MRP: ₹{product.mrp} | Unit: {product.unit}
//                     </div>
//                     <div className="text-xs text-gray-400 mt-1">
//                       HSN: {product.hsncode} | GST: {product.gstpercentage}%
//                     </div>
//                   </div>
//                   <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Add</span>
//                 </div>
//               </li>
//             ))}
//           </ul>
//         ) : (
//           <div className="p-8 text-center text-gray-500">No products found matching your search.</div>
//         )}
//       </div>
//     </div>
//   </div>
// )}
//     </div>
//   );
// };

// export default EditQuotation;
